import { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { Tooltip } from 'primereact/tooltip';
import { Toast } from 'sharedUi/Toast';
import { Button } from 'sharedUi/Button';
import { Dropdown } from 'sharedUi/Dropdown';
import { RefreshButton } from 'sharedUi/RefreshButton';
import { useAppNavigate } from '../../hooks/useAppNavigate';
import { useDebounce } from '../../hooks/useDebounce';
import {
  appConfigService,
  summariseValue,
  type AppConfig,
  type AppConfigDataType,
  type AppConfigQueryParams,
} from '../../services/app-config.service';
import { projectsService, type AccessibleProject } from '../../services/projects.service';
import { extractErrorMessage } from '../../utils/error-handler';
import DeleteConfirmDialog from '../../components/dialogs/delete-confirm-dialog';
import PermissionGuard from '../../components/guards/PermissionGuard';
import { useCanCreate, useCanUpdate, useCanDelete } from '../../hooks/usePermissions';
import { JOB_NAMES } from '../../constants/jobNames';
import PageHeader from '../../components/common/page-header';
import { useAppConfigDataTypes } from './use-app-config-value-sets';
import SaasDataTable, {
  type SaasColumn,
  buildDefaultFilters,
  buildColumnFiltersParam,
} from '../../components/common/saas-data-table';
import { ProjectCell } from '../../components/common/project-cell';

type AppConfigRow = AppConfig & { id: string };

const FILTER_DEFS: SaasColumn[] = [
  { field: 'configKey', header: 'Key', filter: true },
  { field: 'description', header: 'Description', filter: true },
];

const AppConfigList = ({ embedded = false }: { embedded?: boolean }) => {
  const navigate = useAppNavigate();
  const toast = useRef<Toast>(null);
  const canCreate = useCanCreate(JOB_NAMES.APP_CONFIG);
  const canUpdate = useCanUpdate(JOB_NAMES.APP_CONFIG);
  const canDelete = useCanDelete(JOB_NAMES.APP_CONFIG);

  const [configs, setConfigs] = useState<AppConfigRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [deleteTarget, setDeleteTarget] = useState<AppConfigRow | null>(null);

  // Identifies the row SET, not its size: page two can hold the same number of
  // rows as page one, and a key that did not change would leave the tooltip
  // bound to buttons React has already replaced.
  const tooltipKey = useMemo(
    () => configs.map((c) => `${c.appId ?? ''}:${c.configKey}`).join('|'),
    [configs],
  );
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [accessibleProjects, setAccessibleProjects] = useState<AccessibleProject[]>([]);
  /* The screen is always scoped to ONE project. Opening on "everything" mixed a
     project's own rows with the Default (appId: null) rows every project falls
     back to, which made two very different things look like one list — and made
     the common question, "what is set for THIS project", the hard one to answer.

     Seeded from the signed-in project, the same key the background-job settings
     tab reads. It is no longer selectable on the screen: the Default rows remain
     the fallback the platform resolves against, but they are not a thing this
     screen shows or lets anyone edit. */
  const [selectedProjectId, setSelectedProjectId] = useState<string | null>(
    () => localStorage.getItem('appId') || null);
  const [dataTypeFilter, setDataTypeFilter] = useState<AppConfigDataType | null>(null);
  const { options: dataTypeOptions } = useAppConfigDataTypes();
  // Code → value-set label, so the Type column shows the same friendly name the
  // dropdowns do rather than the raw code. Derived from the value set the picker
  // reads, never a hardcoded map — the two can't drift apart.
  const dataTypeLabels = useMemo(
    () => Object.fromEntries(dataTypeOptions.map((o) => [o.value, o.label])),
    [dataTypeOptions],
  );

  /* Whether the project scope has settled. The list must NOT be fetched before
     it has: with no appId the API answers with everything the caller may see —
     every project's rows interleaved with the defaults — which is both the
     widest, slowest query the endpoint can run AND the wrong answer, and it was
     then immediately thrown away when `getAccessible` resolved and refetched
     with the real scope. Seeded true when localStorage already names a project,
     so the common path still fires exactly one request straight away. */
  const [scopeReady, setScopeReady] = useState(() => !!localStorage.getItem('appId'));

  useEffect(() => {
    // Drives the project picker, and supplies an appId when the signed-in one is
    // missing. A caller whose token carries no appId would otherwise send no
    // scope, and the API answers that with every row it may see — including the
    // shared Default rows this screen is not meant to show.
    projectsService.getAccessible()
      .then((rows) => {
        setAccessibleProjects(rows);
        setSelectedProjectId((current) => current || rows[0]?._id || null);
      })
      .catch(() => undefined)
      .finally(() => setScopeReady(true));
  }, []);

  const [searchQuery, setSearchQuery] = useState('');
  const [first, setFirst] = useState(0);
  const [pageSize, setPageSize] = useState(10);
  const [totalRecords, setTotalRecords] = useState(0);
  const [sortField, setSortField] = useState<string | undefined>('configKey');
  const [sortOrder, setSortOrder] = useState<1 | -1 | 0 | undefined>(1);
  const [filters, setFilters] = useState<Record<string, any>>(() => buildDefaultFilters(FILTER_DEFS));

  const debouncedSearchQuery = useDebounce(searchQuery, 500);

  const notify = useCallback((severity: 'success' | 'error', summary: string, detail: string) => {
    toast.current?.show({ severity, summary, detail, life: severity === 'error' ? 10000 : 4000 });
  }, []);

  const fetchConfigs = useCallback(async (
    page = 1,
    size = pageSize,
    field?: string,
    order?: 1 | -1 | 0,
    activeFilters: Record<string, any> = filters,
  ) => {
    try {
      setLoading(true);
      const params: AppConfigQueryParams = { page, pageSize: size };
      if (debouncedSearchQuery) params.search = debouncedSearchQuery;
      if (field && order && (order as number) !== 0) {
        params.sortBy = field;
        params.sortOrder = order === 1 ? 'asc' : 'desc';
      }
      if (dataTypeFilter) params.dataType = dataTypeFilter;
      // Always the caller's own project. The API reads an ABSENT appId as
      // "everything the caller may see", which would mix the shared Default rows
      // into the list — with no scope picker on the screen any more, this is the
      // only thing keeping it to one project's own settings.
      if (selectedProjectId) params.appId = selectedProjectId;

      // The column filters the table produces are sent under the same key the
      // other master lists use; the API ignores what it does not recognise.
      const filtersParam = buildColumnFiltersParam(activeFilters);
      if (filtersParam) params.filters = filtersParam;

      const response = await appConfigService.getAll(params);
      setConfigs((response.data || []).map((c) => ({ ...c, id: c._id })));
      setTotalRecords(response.pagination?.totalItems ?? (response.pagination as any)?.total ?? 0);
    } catch (error) {
      notify('error', 'Error', extractErrorMessage(error, 'Failed to load configuration'));
    } finally {
      setLoading(false);
    }
  }, [debouncedSearchQuery, selectedProjectId, dataTypeFilter, pageSize, filters, notify]);

  const handlePageChange = (event: any) => {
    setFirst(event.first);
    setPageSize(event.rows);
    fetchConfigs(Math.floor(event.first / event.rows) + 1, event.rows, sortField, sortOrder);
  };

  const handleSort = (event: any) => {
    setSortField(event.sortField);
    setSortOrder(event.sortOrder);
    setFirst(0);
    fetchConfigs(1, pageSize, event.sortField, event.sortOrder);
  };

  const handleFilter = (event: any) => {
    const next = event.filters || {};
    setFilters(next);
    setFirst(0);
    fetchConfigs(1, pageSize, sortField, sortOrder, next);
  };

  useEffect(() => {
    if (!scopeReady) return;
    setFirst(0);
    fetchConfigs(1, pageSize, sortField, sortOrder);
  }, [debouncedSearchQuery, selectedProjectId, dataTypeFilter, scopeReady]); // eslint-disable-line react-hooks/exhaustive-deps

  const refresh = () => fetchConfigs(Math.floor(first / pageSize) + 1, pageSize, sortField, sortOrder);

  /** Soft-delete: the row leaves the list, but its record and history are kept
   *  and it stops being read at runtime, so it can be restored later. */
  const handleDeleteConfirm = async () => {
    if (!deleteTarget) return;
    try {
      setDeleteLoading(true);
      await appConfigService.delete(deleteTarget.configKey, deleteTarget.appId ?? undefined);
      notify('success', 'Deleted', `${deleteTarget.configKey} was removed from the list.`);
      setDeleteTarget(null);
      refresh();
    } catch (error) {
      notify('error', 'Error', extractErrorMessage(error, 'Could not delete the configuration'));
    } finally {
      setDeleteLoading(false);
    }
  };

  const columns = useMemo<SaasColumn<AppConfigRow>[]>(() => [
    {
      field: 'configKey',
      header: 'Key',
      filter: true,
      body: (row: AppConfigRow) => (
        <span className="font-mono text-xs">{row.configKey}</span>
      ),
    },
    {
      field: 'description',
      header: 'Description',
      filter: true,
      // Widest column: descriptions are the sentence, so give them the room.
      style: { minWidth: '340px' },
      headerStyle: { minWidth: '340px' },
      // Clamp to two lines so a long description cannot make one row tower over
      // the rest; the full text stays on the view screen and in the tooltip.
      body: (row: AppConfigRow) => (
        <span className="line-clamp-2 text-xs text-gray-600 dark:text-gray-300" title={row.description || ''}>
          {row.description || '—'}
        </span>
      ),
    },
    {
      /* Which project the row belongs to. The list is scoped to one project, but
         the API also returns the shared Default rows it falls back to, so this is
         the column that tells the two apart — ProjectCell renders a null appId as
         "Global". Not hidden behind canScopeByProject: even a single-project user
         needs to see which of these is their override and which is the default. */
      field: 'appId',
      header: 'Project',
      style: { width: '150px' },
      body: (row: AppConfigRow) => <ProjectCell appId={row.appId} projects={accessibleProjects} />,
    },
    {
      field: 'dataType',
      header: 'Type',
      sortable: true,
      // Plain text, like every other cell. The tinted chips per data type turned
      // a reference column into the loudest thing on the row.
      body: (row: AppConfigRow) => (
        <span className="text-sm text-ink">
          {dataTypeLabels[row.dataType] || row.dataType}
        </span>
      ),
    },
    {
      field: 'value',
      header: 'Value',
      // Narrow: most values are short (a number, a flag, masked dots).
      style: { width: '130px', maxWidth: '160px' },
      headerStyle: { width: '130px', maxWidth: '160px' },
      // An ENCRYPTED value is never shown, here or anywhere — `summariseValue`
      // returns dots for it rather than the masked stand-in, so the column stays
      // scannable. A BOOLEAN prints its true/false like any other value; the
      // coloured on/off pill it used to get made the column read as a status.
      //
      // A long OBJECT/STRING must not spill under the action icons — clamp it
      // to the column and keep the full value in the tooltip (and on the view).
      body: (row: AppConfigRow) => {
        const summary = summariseValue(row);
        return (
          <span className="block max-w-[150px] truncate font-mono text-xs" title={String(summary)}>
            {summary}
          </span>
        );
      },
    },
    /* Status and Updated are deliberately absent from the table. Status is still
       reachable — the toolbar filters by it — and both remain on the view screen,
       where there is room to show them with their history. */
    {
      field: 'actions',
      header: 'Actions',
      align: 'center',
      hideable: false,
      body: (row: AppConfigRow) => {
        const suffix = row.appId ? `?appId=${row.appId}` : '';
        const key = encodeURIComponent(row.configKey);
        return (
          <div className="flex gap-1 justify-center">
            <button aria-label="View"
              className="p-2 rounded hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
              onClick={() => navigate(`/app-config/${key}${suffix}`)}
              data-testid="AppConfigList.Action.View"
              data-pr-tooltip="View"
            >
              <i className="pi pi-eye text-green-600 dark:text-green-400" style={{ fontSize: '1.05rem' }} />
            </button>

            {canUpdate && (
              <button aria-label="Edit"
                className="p-2 rounded hover:bg-blue-50 dark:hover:bg-blue-900/20 transition-colors"
                onClick={() => navigate(`/app-config/${key}/edit${suffix}`)}
                data-testid="AppConfigList.Action.Edit"
                data-pr-tooltip="Edit"
              >
                <i className="pi pi-pencil text-blue-600" style={{ fontSize: '1.05rem' }} />
              </button>
            )}

            {/* Soft delete — the row leaves the list but its record and history
                are kept, so this is reversible, not a hard removal.

                Omitted on a PLATFORM row rather than shown and refused: the API
                returns 403 for those, and a control that always fails teaches
                people to ignore errors. Deactivate is still available there, and
                is the supported way to stop a provisioned key being read.

                The slot is RESERVED rather than collapsed. Nearly every row is
                provisioned, so letting the group reflow would leave the handful
                of deletable rows with their view and edit icons sitting a button
                to the left of everyone else's — the eye reads that as misaligned
                long before it reads it as "this one has an extra action". */}
            {canDelete && (
              row.origin !== 'PLATFORM' ? (
                <button aria-label="Delete"
                  className="p-2 rounded hover:bg-red-50 dark:hover:bg-red-900/20 transition-colors"
                  onClick={() => setDeleteTarget(row)}
                  data-testid="AppConfigList.Action.Delete"
                  data-pr-tooltip="Delete"
                >
                  <i className="pi pi-trash text-red-600" style={{ fontSize: '1.05rem' }} />
                </button>
              ) : (
                <span className="p-2" aria-hidden="true">
                  <i className="pi pi-trash invisible" style={{ fontSize: '1.05rem' }} />
                </span>
              )
            )}
          </div>
        );
      },
    },
  ], [canUpdate, canDelete, navigate, selectedProjectId, dataTypeLabels, accessibleProjects]);

  return (
    <PermissionGuard jobName={JOB_NAMES.APP_CONFIG}>
      <div className={embedded ? '' : 'p-6'} data-testid="SCR-AppConfig-List">
        <Toast ref={toast} />
        {/* One instance for the whole screen, rather than the per-row mounting
            some older lists do.

            Keyed on the rows on purpose. PrimeReact resolves this selector ONCE,
            with a plain `DomHandler.find(document, target)` and no
            MutationObserver, and re-binds only when `target` changes — which a
            constant string never does. The rows arrive from the API after this
            mounts, so without the key the buttons are created with no listener
            attached and no hint ever appears. The key remounts it once per row
            set, which is what binds it to the buttons that now exist. */}
        <Tooltip key={tooltipKey} target="[data-pr-tooltip]" />
        {!embedded && (
          <PageHeader
            icon="pi pi-sliders-h"
            title="Application Configuration"
            subtitle="Typed settings an administrator can change without a release. A Default row applies to every project; a project may override it."
            testId="AppConfigList.Header"
          />
        )}

        <div className={embedded ? '' : 'bg-surface rounded-xl shadow-sm border border-line overflow-hidden'}>
          <SaasDataTable
            tableId="app-config"
            columns={columns}
            data={configs}
            loading={loading}
            dataKey="id"
            emptyMessage="No configuration found"
            first={first}
            rows={pageSize}
            totalRecords={totalRecords}
            onPage={handlePageChange}
            rowsPerPageOptions={[5, 10, 25, 50]}
            sortField={sortField || undefined}
            sortOrder={sortOrder || undefined}
            onSort={handleSort}
            filters={filters}
            onFilter={handleFilter}
            globalSearch={searchQuery}
            onGlobalSearchChange={setSearchQuery}
            globalSearchPlaceholder="Search by key or description..."
            testId="AppConfigList"
            toolbarStart={
              <div className="flex flex-wrap items-center gap-3">
                {accessibleProjects.length > 1 && (
                  /* Switches which project's settings are shown. Deliberately
                     without `showClear` and without an "all projects" entry:
                     clearing would send no appId, which the API reads as
                     everything the caller may see — this project's rows and the
                     shared Default rows interleaved, two different things looking
                     like one list. The scope is always exactly one project. */
                  <Dropdown
                    value={selectedProjectId}
                    options={accessibleProjects.map((p) => ({
                      label: p.appCode ? `${p.name} (${p.appCode})` : p.name,
                      value: p._id,
                    }))}
                    onChange={(e: any) => setSelectedProjectId(e.value)}
                    placeholder="Project"
                    style={{ minWidth: '190px' }}
                    data-testid="AppConfigList.Filter.Project"
                  />
                )}
                <Dropdown
                  value={dataTypeFilter}
                  options={dataTypeOptions}
                  onChange={(e: any) => setDataTypeFilter(e.value || null)}
                  placeholder="Type"
                  showClear
                  style={{ minWidth: '160px' }}
                  data-testid="AppConfigList.Filter.DataType"
                />
              </div>
            }
            toolbarEnd={
              <>
                <span className="whitespace-nowrap text-sm text-ink-muted">
                  {totalRecords} {totalRecords === 1 ? 'record' : 'records'}
                </span>
                {/* The Integration Providers link is intentionally not here for
                    now — it is to be surfaced from wherever gateway setup ends up
                    living. The screen itself is untouched and still served at
                    /app-config/providers (and /app-config/providers/:channel);
                    only the entry point from this toolbar is withheld, so putting
                    it back is one button, not a rebuild. */}
                <RefreshButton onClick={refresh} loading={loading} testId="AppConfigList.Button.Refresh" />
                {canCreate && (
                  <Button
                    label="Create Configuration"
                    icon="pi pi-plus"
                    onClick={() => navigate(
                      selectedProjectId
                        ? `/app-config/create?appId=${selectedProjectId}`
                        : '/app-config/create')}
                    testId="AppConfigList.Button.Create"
                    size="small"
                  />
                )}
              </>
            }
          />
        </div>

        <DeleteConfirmDialog
          visible={!!deleteTarget}
          onCancel={() => setDeleteTarget(null)}
          onDelete={handleDeleteConfirm}
          // Soft delete: the row leaves the list and stops being read at runtime,
          // but its record and history are kept, so it can be restored and a key
          // still referenced resolves to a controlled error rather than vanishing.
          message={`Delete "${deleteTarget?.configKey}"? It is removed from the list and stops being read at runtime. Its record and history are kept, so it can be restored.${deleteTarget?.appId ? '' : ' This is a Default row, so it affects every project that has no override.'}`}
          testId="AppConfigList.Dialog.Delete"
          loading={deleteLoading}
        />
      </div>
    </PermissionGuard>
  );
};

export default AppConfigList;
